import { useWindowDimensions } from 'hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDebouncedCallback, useThrottledCallback } from 'use-debounce'

interface IProps {
  itemWidth: number
  itemsCount: number
}

export const leftSideSpacing = 40 // because of -mx-10 & pl-10 in carousel
const xAxisSpacing = 24 // because of spaxe-x-6 in carousel

function usePagedScrolls({ itemWidth, itemsCount }: IProps) {
  const screen = useWindowDimensions()

  const screenWidth = screen.width || 1000
  const [currentPage, setCurrentPage] = useState(0)
  const visibleItemsPerPage = Math.floor(
    Math.max((screenWidth - leftSideSpacing) / (itemWidth + xAxisSpacing), 1)
  )
  const pageWidthWithItemSpacing =
    itemWidth * visibleItemsPerPage + (visibleItemsPerPage - 1) * xAxisSpacing

  const numberOfPages = useMemo(() => {
    if (!pageWidthWithItemSpacing) {
      return 0
    }
    const count = Math.ceil(itemsCount / visibleItemsPerPage)
    if (!count || !Number.isFinite(count) || count < 0) {
      return 0
    }
    return count
  }, [pageWidthWithItemSpacing, itemsCount, visibleItemsPerPage])

  const [scrollX, setScrollX] = useState<number>(0)
  const [allowUpdateFromScrolling, setAllowUpdateFromScrolling] =
    useState<boolean>(true)

  useEffect(() => {
    // prevent page jumping when page is updated from scrolling instead
    if (allowUpdateFromScrolling) {
      return
    }
    const spacingOffset = currentPage * xAxisSpacing
    setScrollX(pageWidthWithItemSpacing * currentPage + spacingOffset)
  }, [allowUpdateFromScrolling, currentPage, pageWidthWithItemSpacing])

  const resetScrolling = useDebouncedCallback(() => {
    setAllowUpdateFromScrolling(true)
  }, 1000)

  const setCurrentPageWrapper = useThrottledCallback(page => {
    setAllowUpdateFromScrolling(false)
    setCurrentPage(page)
    resetScrolling()
  }, 300)

  const prevPage = useCallback(() => {
    setCurrentPageWrapper((x: number) => Math.max(0, x - 1))
  }, [setCurrentPageWrapper])

  const nextPage = useCallback(() => {
    setCurrentPageWrapper((x: number) => Math.min(x + 1, numberOfPages - 1))
  }, [numberOfPages, setCurrentPageWrapper])

  const [maxScrollX, setMaxScrollX] = useState(0)
  const updateCurrentPageMarker = useCallback(
    (newScrollX: number) => {
      // prevent page jumping because next and prev buttons change scrollX
      if (!allowUpdateFromScrolling) {
        return
      }

      const pageNum = newScrollX / (pageWidthWithItemSpacing + xAxisSpacing)
      const page = Math.round(pageNum) // causes a change once page has averagedly changed

      if (
        page !== currentPage &&
        page > currentPage &&
        pageNum >= currentPage + 0.66 // change a little later than half point of previous page, higher number = later
      ) {
        setCurrentPage(page)
      } else if (
        page !== currentPage &&
        page < currentPage &&
        pageNum <= currentPage - 0.5 // change a little later than half point of previous page, higher number = later
      ) {
        setCurrentPage(page)
      } else if (newScrollX === maxScrollX && numberOfPages > 0) {
        setCurrentPage(numberOfPages - 1) //incase last page is too small to read +.66
      }
    },
    [
      allowUpdateFromScrolling,
      pageWidthWithItemSpacing,
      currentPage,
      maxScrollX,
      numberOfPages
    ]
  )

  return {
    numberOfPages,
    scrollX,
    prevPage,
    nextPage,
    currentPage,
    setCurrentPage: setCurrentPageWrapper,
    updateCurrentPageMarker,
    setMaxScrollX
  }
}

export default usePagedScrolls
